import { defaultFormValues } from '@brands/Dashboard/MyProfile/Patient/utils/defaultFormValues';
import { MyProfileSubmit } from '@brands/Dashboard/MyProfile/Patient/utils/types';
import { Insurance, UserProfile } from '@brands/services/identity/types/UserProfile';
import { createAction } from '@reduxjs/toolkit';
import moment from 'moment';

import { UserCompleteProfileForm } from '../../Auth/SignUp/types';

export const fetchPatientMyProfileStart = createAction('patientMyProfile/fetchStart');
export const fetchPatientMyProfileSuccess = createAction(
  'patientMyProfile/fetchSuccess',
  (profile: MyProfileSubmit) => ({ payload: profile }),
);
export const fetchPatientMyProfileError = createAction('patientMyProfile/fetchError', (error: string) => ({
  payload: error,
}));

export const mapProfileToMyProfileSubmit = (
  patientProfile: UserProfile | undefined,
  patientInsurance?: Insurance[],
  newDependent?: boolean,
): MyProfileSubmit => {
  if (patientProfile === undefined) return defaultFormValues;
  const primaryInsurance = patientInsurance?.find((i) => !i.is_secondary);
  const secondaryInsurance = patientInsurance?.find((i) => i.is_secondary);
  const mappedProfile: MyProfileSubmit = {
    ...defaultFormValues,
    patientId: patientProfile.id,
    date_of_birth: patientProfile.basic_info?.date_of_birth
      ? moment(patientProfile.basic_info?.date_of_birth).format('MM/DD/YYYY')
      : '',
    first_name: patientProfile.basic_info?.first_name || '',
    middle_name: patientProfile.basic_info?.middle_name || '',
    last_name: patientProfile.basic_info?.last_name || '',
    preferred_name: patientProfile.basic_info?.preferred_name || '',
    phone_number: patientProfile?.phone_number
      ? patientProfile.phone_number.replace(/^(\+\d)(\d{3})(\d{3})(\d{4})$/, '$1 ($2) $3 $4')
      : '',
    email: patientProfile?.email || '',
    external_id: patientProfile?.external_id || '',
    address1: patientProfile.address?.address1 || '',
    address2: patientProfile.address?.address2 || '',
    zip_code: patientProfile.address?.zip_code || '',
    state: patientProfile.address?.state || '',
    city: patientProfile.address?.city || '',
    languages: patientProfile.basic_info?.languages || [''],
    language: [],
    gender: patientProfile.basic_info?.gender || '',
    pronouns: patientProfile.basic_info?.pronouns || '',
    state_funded_coverage_status:
      patientInsurance && primaryInsurance ? primaryInsurance.state_funded_coverage_status : '',
    gender_identity: patientProfile.basic_info?.gender_identity || '',
    insurance_through: patientInsurance && primaryInsurance?.insured_by ? primaryInsurance.insured_by : 'none',
    member_id:
      patientInsurance && primaryInsurance?.insurance_membership
        ? primaryInsurance?.insurance_membership.member_id
        : '',
    group_number:
      patientInsurance && primaryInsurance?.insurance_membership
        ? primaryInsurance?.insurance_membership.group_number
        : '',
    insurance_address:
      patientInsurance && primaryInsurance?.insurance_membership
        ? primaryInsurance?.insurance_membership.address?.address1
        : '',
    insurance_city:
      patientInsurance && primaryInsurance?.insurance_membership
        ? primaryInsurance?.insurance_membership.address?.city
        : '',
    insurance_state:
      patientInsurance && primaryInsurance?.insurance_membership
        ? primaryInsurance?.insurance_membership.address?.state
        : '',
    insurance_zip_code:
      patientInsurance && primaryInsurance?.insurance_membership
        ? primaryInsurance?.insurance_membership.address?.zip_code
        : '',
    iFirstName: (patientInsurance && primaryInsurance?.insurance_through?.first_name) || '',
    iMiddleName: (patientInsurance && primaryInsurance?.insurance_through?.middle_name) || '',
    iLastName: (patientInsurance && primaryInsurance?.insurance_through?.last_name) || '',
    iDateOfBirth:
      patientInsurance && primaryInsurance?.insurance_through
        ? moment(primaryInsurance?.insurance_through?.date_of_birth).format('MM/DD/YYYY')
        : '',
    iGenderIdentity: (patientInsurance && primaryInsurance?.insurance_through?.gender_identity) || '',
    iAddress1: (patientInsurance && primaryInsurance?.insurance_through?.address?.address1) || '',
    iAddress2: (patientInsurance && primaryInsurance?.insurance_through?.address?.address2) || '',
    iZipCode: (patientInsurance && primaryInsurance?.insurance_through?.address?.zip_code) || '',
    iState: (patientInsurance && primaryInsurance?.insurance_through?.address?.state) || '',
    iCity: (patientInsurance && primaryInsurance?.insurance_through?.address?.city) || '',
    iPhoneNumber:
      patientInsurance && primaryInsurance?.insurance_membership
        ? primaryInsurance?.insurance_membership.phone_number
        : '',
    insurance_carrier_id: patientInsurance && primaryInsurance?.insurance_membership?.insurance_carrier_id,

    secondary_insurance_through:
      patientInsurance && secondaryInsurance?.insured_by ? secondaryInsurance.insured_by : 'none',
    secondary_member_id:
      patientInsurance && secondaryInsurance?.insurance_membership
        ? secondaryInsurance.insurance_membership.member_id
        : '',
    secondary_group_number:
      patientInsurance && secondaryInsurance?.insurance_membership
        ? secondaryInsurance.insurance_membership.group_number
        : '',
    secondary_iFirstName: (patientInsurance && secondaryInsurance?.insurance_through?.first_name) || '',
    secondary_iMiddleName: (patientInsurance && secondaryInsurance?.insurance_through?.middle_name) || '',
    secondary_iLastName: (patientInsurance && secondaryInsurance?.insurance_through?.last_name) || '',
    secondary_iDateOfBirth:
      patientInsurance && secondaryInsurance?.insurance_through
        ? moment(secondaryInsurance?.insurance_through?.date_of_birth).format('MM/DD/YYYY')
        : '',
    secondary_iGenderIdentity: (patientInsurance && secondaryInsurance?.insurance_through?.gender_identity) || '',
    secondary_iAddress1: (patientInsurance && secondaryInsurance?.insurance_through?.address?.address1) || '',
    secondary_iAddress2: (patientInsurance && secondaryInsurance?.insurance_through?.address?.address2) || '',
    secondary_iZipCode: (patientInsurance && secondaryInsurance?.insurance_through?.address?.zip_code) || '',
    secondary_iState: (patientInsurance && secondaryInsurance?.insurance_through?.address?.state) || '',
    secondary_iCity: (patientInsurance && secondaryInsurance?.insurance_through?.address?.city) || '',
    secondary_iPhoneNumber:
      patientInsurance && secondaryInsurance?.insurance_membership
        ? secondaryInsurance.insurance_membership.phone_number
        : '',
    secondary_insurance_carrier_id: patientInsurance && secondaryInsurance?.insurance_membership?.insurance_carrier_id,
    secondary_insurance_address:
      patientInsurance && secondaryInsurance?.insurance_membership
        ? secondaryInsurance?.insurance_membership.address?.address1
        : '',
    secondary_insurance_city:
      patientInsurance && secondaryInsurance?.insurance_membership
        ? secondaryInsurance?.insurance_membership.address?.city
        : '',
    secondary_insurance_state:
      patientInsurance && secondaryInsurance?.insurance_membership
        ? secondaryInsurance?.insurance_membership.address?.state
        : '',
    secondary_insurance_zip_code:
      patientInsurance && secondaryInsurance?.insurance_membership
        ? secondaryInsurance?.insurance_membership.address?.zip_code
        : '',
    organizations:
      patientProfile.organizations?.map((org) => {
        const matchingDivision = patientProfile.patient_profile?.divisions?.find(
          (division) => division?.organization_id === org.id,
        );

        const matchingPractice = patientProfile.patient_profile?.practices?.find(
          (practice) => practice?.organization_id === org.id,
        );

        return {
          ...org,
          divisions: matchingDivision ? [matchingDivision] : [],
          practices: matchingPractice ? [matchingPractice] : [],
        };
      }) || [],
    organization: [],
    ...(newDependent && { dependent_address1: patientProfile.address?.address1 }),
    ...(newDependent && { dependent_address2: patientProfile.address?.address2 }),
    ...(newDependent && { dependent_zip_code: patientProfile.address?.zip_code }),
    ...(newDependent && { dependent_state: patientProfile.address?.state }),
    ...(newDependent && { dependent_city: patientProfile.address?.city }),
  };
  return mappedProfile;
};

export const mapProfileToNewMyProfileSubmit = (patientProfile: UserProfile | undefined): UserCompleteProfileForm => {
  if (patientProfile === undefined) return defaultFormValues;
  const mappedProfile: UserCompleteProfileForm = {
    id: patientProfile?.id || 0,
    date_of_birth: patientProfile.basic_info?.date_of_birth
      ? moment(patientProfile.basic_info?.date_of_birth).format('MM/DD/YYYY')
      : '',
    first_name: patientProfile.basic_info?.first_name || '',
    middle_name: patientProfile.basic_info?.middle_name || '',
    last_name: patientProfile.basic_info?.last_name || '',
    preferred_name: patientProfile.basic_info?.preferred_name || '',
    phone_number: patientProfile?.phone_number
      ? patientProfile.phone_number.replace(/^(\+\d)(\d{3})(\d{3})(\d{4})$/, '$1 ($2) $3 $4')
      : '',
    address1: patientProfile.address?.address1 || '',
    address2: patientProfile.address?.address2 || '',
    zip_code: patientProfile.address?.zip_code || '',
    state: patientProfile.address?.state || '',
    city: patientProfile.address?.city || '',
    languages: patientProfile.basic_info?.languages || [''],
    language: [],
    gender: patientProfile.basic_info?.gender || '',
    pronouns: patientProfile.basic_info?.pronouns || '',
    gender_identity: patientProfile.basic_info?.gender_identity || '',
  };
  return mappedProfile;
};
